import React from 'react';
import HeroImage from '@/assets/hero_img.png';
import becomeTutorImage from '@/assets/become_tutor/become_tutor.png';
import { useModal } from '@/contexts';
import { useI18n } from '@/i18n';

// ── Page ─────────────────────────────────────────────────────────────────────

const AboutUsPage: React.FC = () => {
  const { openParentRegistration, openTutorRegistration } = useModal();
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';

  const founderMessages = [
    {
      name: 'Louis',
      role: isZh ? '联合创办人' : 'Co-founder',
      summary: isZh ? '合适的引导，足以彻底改变学生的信心与学习进步。' : 'The right guidance can completely change a student’s confidence and academic progress.',
      details: isZh ? [
        '我自己的学习经历让我明白，很多学生并不是没有能力，他们更需要的是更清楚的讲解、更有力的鼓励，以及真正符合自己学习方式的支持。',
        '大学期间的补习经历也让我看到，个性化教学确实能同时提升理解与成绩。Tutor Founder 就是在这样的想法下诞生，希望让更多家庭更容易获得值得信赖、真正有效的学习支持。',
      ] : [
        'My own learning journey showed me that many students do not lack ability. What they often need is a clearer explanation, stronger encouragement, and support that matches the way they learn.',
        'Through tutoring during university, I saw how personalised teaching could improve both understanding and results. Tutor Founder was created to make that kind of trusted, effective support more accessible to more families.',
      ],
    },
    {
      name: 'Hanson',
      role: isZh ? '联合创办人' : 'Co-founder',
      summary: isZh ? '为每个家庭找到合适导师，应该是透明、可信而且直接的。' : 'Finding the right tutor should be transparent, credible, and straightforward for every family.',
      details: isZh ? [
        '我们不断看到同一个问题在家长与导师两边重复出现。家长很难有信心地判断导师是否合适，而真正有实力的教育者也缺少一个专业平台去清楚表达自己的优势。',
        'Tutor Founder 的建立，就是为了缩短这段距离，让家庭与导师之间的连接更可靠、更容易。这个原则也一直在影响我们今天如何设计整个平台。',
      ] : [
        'We kept seeing the same issue from both sides. Parents found it difficult to evaluate tutors with confidence, while capable educators lacked a professional platform to present their strengths clearly.',
        'Tutor Founder was built to close that gap by creating a more reliable and accessible way for families and tutors to connect. That principle continues to guide how we design the platform today.',
      ],
    },
  ];

  const values = [
    {
      title: isZh ? '信任与安全' : 'Trust & Safety',
      description:
        isZh ? '每位导师都会经过仔细审核，让家庭从一开始就更安心。' : 'Every tutor is carefully reviewed, so families can feel more confident from the start.',
    },
    {
      title: isZh ? '真正合适的配对' : 'The Right Match',
      description:
        isZh ? '我们不只看科目和时间，也更重视导师是否真正适合每位学生的需求。' : 'We look beyond subjects and schedules to find tutors who truly fit each student’s needs.',
    },
    {
      title: isZh ? '灵活安排' : 'Flexibility',
      description:
        isZh ? '学习应该配合真实生活，有足够灵活的选择来适应不同时间与偏好。' : 'Learning should fit real life, with options that work around different schedules and preferences.',
    },
    {
      title: isZh ? '真实进步' : 'Real Progress',
      description:
        isZh ? '我们重视稳定进步、更强的信心，以及真正带来改变的课堂。' : 'We care about steady improvement, stronger confidence, and lessons that make a real difference.',
    },
  ];

  const principleGradients = [
    'bg-[linear-gradient(145deg,#eef5eb_0%,#f8fbf4_55%,#e7f1ea_100%)]',
    'bg-[linear-gradient(145deg,#f4f7ea_0%,#fbfcf8_52%,#edf3e4_100%)]',
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* ── Hero ── */}
      <section className="flex items-start overflow-hidden bg-white md:min-h-[100svh]">
        <div className="container mx-auto px-6 pb-2 pt-8 md:px-12 md:pb-8 md:pt-10">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl font-black leading-tight tracking-tight text-primary sm:text-5xl md:text-6xl lg:text-7xl">
                {isZh ? '把学生与真正合适的导师连接起来' : 'Connecting Students with the Right Tutors'}
              </h1>
              <p className="mx-auto mt-6 max-w-3xl text-base leading-8 text-gray-600 md:text-lg">
                {isZh
                  ? '在 Tutor Founder，我们相信当合适的学生遇到合适的导师时，学习才能真正发挥效果。我们正在打造一个值得信赖的平台，让每个家庭都能更轻松、更安心、更有温度地找到所需支持。'
                  : 'At Tutor Founder, we believe learning works best when the right student meets the right tutor. We are building a trusted platform that makes finding support easier, safer, and more personal for every family.'}
              </p>
            </div>

            <div className="mt-8 md:mt-10">
              <div className="flex min-h-[300px] items-center justify-center md:min-h-[420px]">
                <div
                  className="overflow-hidden rounded-3xl shadow-xl"
                  style={{ boxShadow: '0 24px 80px rgba(15, 23, 42, 0.08)' }}
                >
                  <img
                    src={becomeTutorImage}
                    alt={isZh ? 'Tutor Founder 导师加入流程' : 'Tutor Founder tutor onboarding'}
                    className="block h-auto max-h-[72vh] w-auto max-w-full rounded-3xl object-contain md:max-h-[640px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Co-Founders ── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1360px] px-4 sm:px-6 md:px-10 lg:px-12">
            <div className="text-center">
              <h2 className="text-4xl font-black leading-tight tracking-tight text-primary sm:text-5xl md:text-6xl lg:text-7xl">
                {isZh ? '来自联合创办人的话' : 'A message from our co-founders'}
              </h2>
              <div className="mt-4 flex items-center justify-center gap-3 text-accent">
                <span className="h-px w-10 bg-current/45" />
                <span className="h-1.5 w-14 rounded-full bg-current/75" />
                <span className="h-px w-10 bg-current/45" />
              </div>
            </div>

            <div className="mt-10 space-y-5 md:mt-12 md:space-y-8">
              {founderMessages.map((founder) => (
                <article
                  key={founder.name}
                  className="rounded-[2rem] bg-white p-4 shadow-[0_10px_35px_rgba(15,23,42,0.08)] md:rounded-[2.5rem] md:p-6"
                >
                  <div className="flex flex-col justify-center px-2 py-2 md:px-4 lg:px-6">
                    {/*
                    <div>
                      Founder image block temporarily hidden per current design direction.
                    </div>
                    */}
                    <div className="mx-auto w-full max-w-[44rem]">
                      <div className="font-serif text-6xl leading-none text-secondary md:text-7xl">
                        “
                      </div>
                      <div className="mt-3 max-w-[38rem] space-y-5 text-slate-700">
                        <p className="text-lg leading-9 md:text-[1.4rem] md:leading-[2.1rem] lg:text-[1.5rem] lg:leading-[2.2rem]">
                          <span className="font-bold text-slate-900">{founder.summary}</span>
                        </p>
                        {founder.details.map((detail) => (
                          <p
                            key={detail}
                            className="text-base leading-8 md:text-lg md:leading-9"
                          >
                            {detail}
                          </p>
                        ))}
                      </div>
                      <div className="mt-8">
                        <h3 className="font-serif text-4xl font-semibold text-primary md:text-[3.25rem]">
                          {founder.name}
                        </h3>
                        <p className="mt-1 text-xl font-medium text-slate-700">
                          {founder.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
        </div>
      </section>

      {/* ── Core Values ── */}
      <section className="bg-white py-20 md:py-28 overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl">
          <div className="mx-auto max-w-5xl">
            <div className="text-center">
              <h2 className="text-4xl font-black leading-tight tracking-tight text-primary sm:text-5xl md:text-6xl">
                {isZh ? '我们的原则' : 'Our Principles'}
              </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-4 md:mt-14 md:grid-cols-2 md:gap-5">
              {values.map((value, index) => (
                <article
                  key={value.title}
                  className={`flex h-[240px] flex-col rounded-[2rem] px-7 py-7 shadow-sm transition-transform duration-300 hover:-translate-y-1 md:h-[280px] md:px-9 md:py-7 ${principleGradients[index % principleGradients.length]}`}
                >
                  <h3 className="max-w-[10ch] text-[2rem] font-black leading-[0.98] tracking-tight text-black sm:text-[2.35rem] md:text-[3.1rem]">
                    {value.title}
                  </h3>
                  <p className="mt-auto max-w-[24rem] text-base leading-8 text-stone-700 md:text-[1.05rem] md:leading-8">
                    {value.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Call To Action ── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto w-full max-w-6xl px-0 sm:px-6 md:px-12">
          <div className="relative overflow-hidden rounded-none bg-[linear-gradient(135deg,#f2f8ec_0%,#f8fbf5_50%,#edf6ef_100%)] px-4 py-5 shadow-[0_24px_70px_rgba(47,82,73,0.10)] sm:rounded-[2rem] sm:px-5 md:px-8 md:py-8 lg:px-10 lg:py-10">
            <div className="pointer-events-none absolute -right-16 top-10 h-40 w-40 rounded-full bg-secondary/10 blur-3xl" />
            <div className="pointer-events-none absolute bottom-0 left-1/3 h-32 w-32 rounded-full bg-accent/10 blur-3xl" />

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] lg:items-center lg:gap-10">
              <div className="overflow-hidden rounded-[1.5rem] bg-white/70 shadow-[0_12px_30px_rgba(47,82,73,0.10)] sm:rounded-[1.75rem]">
                <img
                  src={HeroImage}
                  alt={isZh ? '学生通过 Tutor Founder 进行线上学习' : 'Student learning online with Tutor Founder'}
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="w-full px-0 py-2 md:px-2 lg:py-4">
                <h2 className="mt-6 block w-full max-w-full text-4xl font-black leading-[1.05] tracking-tight text-black sm:text-5xl lg:max-w-[11ch] lg:text-6xl">
                  {isZh ? '和合适的导师一起开始学习旅程。' : 'Start the journey with the right tutor.'}
                </h2>

                <p className="mt-6 w-full max-w-full text-lg leading-8 text-gray-600 md:text-xl lg:max-w-xl">
                  {isZh
                    ? '找到真正理解你、并在每一步帮助你持续进步的导师。'
                    : 'Find a tutor who understands you and helps you achieve more, every step of the way.'}
                </p>

                <div className="mt-8 flex w-full flex-col gap-3 md:flex-row md:items-center">
                  <button
                    onClick={openParentRegistration}
                    className="inline-flex min-h-12 w-full items-center justify-center rounded-2xl bg-[#0f6a43] px-8 py-4 text-base font-semibold text-white shadow-[0_16px_32px_rgba(15,106,67,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#0c5938] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0f6a43]/30 md:w-auto"
                  >
                    {isZh ? '立即体验 Tutor Founder' : 'Try Tutor Founder'}
                  </button>
                  <button
                    onClick={() => openTutorRegistration()}
                    className="inline-flex min-h-12 w-full items-center justify-center rounded-2xl border-2 border-[#8db7a1] bg-white/80 px-8 py-4 text-base font-semibold text-primary transition-colors duration-200 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0f6a43]/20 md:w-auto"
                  >
                    {isZh ? '加入我们' : 'Join Us'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUsPage;
